import React, { useMemo, useState } from 'react';
import CardContainer from '../../card/card-container.jsx';
import UsoSirioKpis from './uso-sirio-kpis.jsx';
import UsoSirioBarras from './uso-sirio-barras.jsx';
import UsoSirioTabla from './uso-sirio-tabla.jsx';
import { Colors } from '../../../constants/Colors.js';

const VISTAS = [
  {
    key: 'usuario',
    label: 'Acopios por tipo de usuario',
    series: [
      { key: 'acopios_app', label: 'App' },
      { key: 'acopios_web', label: 'Web' },
      { key: 'acopios_otro', label: 'Otro' },
    ],
    nota: 'App = capturados por acopiadores · Web = capturados por la administración de la cooperativa.',
  },
  {
    key: 'pagos',
    label: 'Pagos registrados',
    series: [
      { key: 'pagos_acopio', label: 'De acopio' },
      { key: 'pagos_anticipo_con_acopio', label: 'Anticipo c/acopio' },
      { key: 'pagos_anticipo_efectivo', label: 'Anticipo en efectivo' },
      { key: 'pagos_remanente', label: 'Remanente' },
    ],
    nota: 'Un acopio puede tener más de un pago, por eso los pagos pueden superar al número de acopios.',
  },
  {
    key: 'operacion',
    label: 'Anticipos, lotes y efectivo',
    series: [
      { key: 'anticipos', label: 'Anticipos' },
      { key: 'lotes', label: 'Lotes' },
      { key: 'registros_efectivo', label: 'Registros de efectivo' },
    ],
    nota: 'Los lotes y los registros de efectivo se atribuyen a la cooperativa a través del centro de acopio.',
  },
];

const COLUMNAS = [
  { key: 'cooperativa', label: 'Cooperativa', tipo: 'texto' },
  { key: 'acopios', label: 'Acopios', tipo: 'numero' },
  { key: 'acopios_app', label: 'App', tipo: 'numero' },
  { key: 'acopios_web', label: 'Web', tipo: 'numero' },
  { key: 'acopios_otro', label: 'Otro', tipo: 'numero' },
  { key: 'anticipos', label: 'Anticipos', tipo: 'numero' },
  { key: 'lotes', label: 'Lotes', tipo: 'numero' },
  { key: 'pagos_acopio', label: 'Pagos acopio', tipo: 'numero' },
  { key: 'pagos_anticipo_con_acopio', label: 'Pagos antic. c/acopio', tipo: 'numero' },
  { key: 'pagos_anticipo_efectivo', label: 'Pagos antic. efvo.', tipo: 'numero' },
  { key: 'pagos_remanente', label: 'Pagos remanente', tipo: 'numero' },
  { key: 'registros_efectivo', label: 'Registros efvo.', tipo: 'numero' },
];

const UsoSirioAcopio = ({ datos }) => {
  const [vistaKey, setVistaKey] = useState(VISTAS[0].key);
  const vista = VISTAS.find(opcion => opcion.key === vistaKey);

  const filas = datos.rows || [];
  const campos = datos.campos || [];

  const totales = useMemo(() => {
    const suma = campo => filas.reduce((acc, fila) => acc + (fila[campo] || 0), 0);
    return {
      acopios: suma('acopios'),
      app: suma('acopios_app'),
      pagos: suma('pagos_acopio') + suma('pagos_anticipo_con_acopio') + suma('pagos_anticipo_efectivo') + suma('pagos_remanente'),
      conActividad: filas.filter(fila => campos.some(campo => fila[campo] > 0)).length,
    };
  }, [filas, campos]);

  // Para la gráfica, `total` es la suma de las series de la vista elegida
  const filasVista = useMemo(
    () =>
      filas.map(fila => ({
        ...fila,
        total: vista.series.reduce((acc, serie) => acc + (fila[serie.key] || 0), 0),
      })),
    [filas, vista]
  );

  // El acopio de un ciclo arranca meses después que el SIC: en vez de 80 renglones
  // en cero, se dice por qué está vacío.
  if (totales.conActividad === 0) {
    return (
      <CardContainer>
        <h6 className="font-lato-normal-500 heading-box mb-2">Sin registros de acopio en este ciclo</h6>
        <p className="mb-0 text-muted" style={{ fontSize: '0.85rem' }}>
          Ninguna cooperativa ha registrado acopios, anticipos, lotes ni pagos en el ciclo {datos.ciclo?.nombre}. El acopio suele arrancar
          varios meses después del levantamiento SIC; revisa el ciclo anterior para ver la temporada completa.
        </p>
      </CardContainer>
    );
  }

  return (
    <>
      <UsoSirioKpis
        items={[
          { label: 'Cooperativas con acopio', value: totales.conActividad, hint: `de ${filas.length} en total` },
          { label: 'Acopios registrados', value: totales.acopios.toLocaleString('es-MX') },
          {
            label: 'Capturados desde la app',
            value: totales.acopios ? `${Math.round((totales.app / totales.acopios) * 100)}%` : '—',
            hint: `${totales.app.toLocaleString('es-MX')} acopios`,
          },
          { label: 'Pagos registrados', value: totales.pagos.toLocaleString('es-MX') },
        ]}
      />

      <CardContainer style={{ marginBottom: 16 }}>
        <div className="d-flex flex-wrap align-items-center justify-content-between mb-3" style={{ gap: 10 }}>
          <h6 className="font-lato-normal-500 heading-box mb-0">{vista.label}</h6>
          <select
            className="form-control select-ciclo"
            style={{ maxWidth: 280 }}
            value={vistaKey}
            onChange={event => setVistaKey(event.target.value)}>
            {VISTAS.map(opcion => (
              <option key={opcion.key} value={opcion.key}>
                {opcion.label}
              </option>
            ))}
          </select>
        </div>
        <UsoSirioBarras series={vista.series} filas={filasVista} etiquetaTotal="registros de acopio" />
        <small className="d-block mt-2" style={{ color: Colors.neutral_400 }}>
          {vista.nota}
        </small>
      </CardContainer>

      <CardContainer>
        <h6 className="font-lato-normal-500 heading-box mb-3">Desglose completo</h6>
        <UsoSirioTabla columnas={COLUMNAS} filas={filas} hoja="Acopio" nombreArchivo={`uso_sirio_acopio_ciclo_${datos.ciclo?.id ?? ''}`} />
      </CardContainer>
    </>
  );
};

export default UsoSirioAcopio;
